import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

type PopupPosition = "top" | "bottom";
type PopupAlign = "start" | "center" | "end";

const DEFAULT_OFFSET = 8;
const VIEWPORT_PADDING = 8;
const PLACEMENT_TOP_CLASS = "core-popup--placement-top";
const PLACEMENT_BOTTOM_CLASS = "core-popup--placement-bottom";
const FIT_CONTENT_CLASS = "core-popup--fitContent";

export class CorePopupPlugin implements Plugin {
  private static openedInstance: CorePopupPlugin | null = null;
  private popupElement: HTMLElement | null = null;
  /** Min-width “de diseño” leído del CSS, sin el clamp inline del layout anterior. */
  private baseMinWidthPx: number | null = null;
  private isOpen = false;
  private placeholderNode: Comment | null = null;
  private originalParent: ParentNode | null = null;
  private boundToggle: (event: Event) => void;
  private boundClickOutside: (event: Event) => void;
  private boundReposition: () => void;
  private boundKeydown: (event: KeyboardEvent) => void;
  private boundCloseButtonClick: (event: Event) => void;

  constructor(private readonly triggerElement: HTMLElement) {
    this.boundToggle = this.toggle.bind(this);
    this.boundClickOutside = this.handleClickOutside.bind(this);
    this.boundReposition = this.reposition.bind(this);
    this.boundKeydown = this.handleKeydown.bind(this);
    this.boundCloseButtonClick = this.handleCloseButtonClick.bind(this);
  }

  init(): void {
    const popupId = this.triggerElement.dataset.corePopupId ?? "";
    if (!popupId) {
      console.warn("CorePopup: trigger must have data-core-popup-id");
      return;
    }

    this.popupElement = document.getElementById(popupId);
    if (!this.popupElement) {
      console.warn(`CorePopup: popup with id "${popupId}" not found`);
      return;
    }

    this.originalParent = this.popupElement.parentNode;
    this.placeholderNode = document.createComment(`core-popup:${popupId}`);
    this.originalParent?.insertBefore(this.placeholderNode, this.popupElement);
    document.body.appendChild(this.popupElement);

    this.popupElement.setAttribute("aria-hidden", "true");
    this.setPlacementClass("bottom");
    this.refreshBaseMinWidthFromCss();

    this.triggerElement.setAttribute("aria-expanded", "false");
    this.triggerElement.setAttribute("aria-controls", popupId);

    this.triggerElement.addEventListener("click", this.boundToggle);
    this.popupElement.addEventListener("click", this.boundCloseButtonClick);
    window.addEventListener("resize", this.boundReposition);
    window.addEventListener("scroll", this.boundReposition, true);
    document.addEventListener("click", this.boundClickOutside);
    document.addEventListener("keydown", this.boundKeydown);
  }

  /** Cierra el popup al pulsar cualquier elemento interno marcado con `data-core-popup-close`. */
  private handleCloseButtonClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (target.closest("[data-core-popup-close]")) {
      this.close();
    }
  }

  private toggle(event: Event): void {
    if (
      this.triggerElement instanceof HTMLAnchorElement &&
      this.triggerElement.getAttribute("href")
    ) {
      event.preventDefault();
    }
    event.stopPropagation();

    if (this.isOpen) {
      this.close();
      return;
    }
    this.open();
  }

  private open(): void {
    if (!this.popupElement) return;

    this.closeOtherOpenedPopup();

    this.isOpen = true;
    this.refreshBaseMinWidthFromCss();
    this.reposition();
    this.popupElement.setAttribute("aria-hidden", "false");
    this.triggerElement.setAttribute("aria-expanded", "true");
    CorePopupPlugin.openedInstance = this;
  }

  private closeOtherOpenedPopup(): void {
    const other = CorePopupPlugin.openedInstance;
    if (other && other !== this) {
      other.close();
    }
  }

  private close(): void {
    if (!this.popupElement) return;

    this.isOpen = false;
    this.popupElement.setAttribute("aria-hidden", "true");
    this.triggerElement.setAttribute("aria-expanded", "false");
    if (CorePopupPlugin.openedInstance === this) {
      CorePopupPlugin.openedInstance = null;
    }
  }

  private getPreferredPosition(): PopupPosition {
    return this.triggerElement.dataset.corePopupPosition === "top"
      ? "top"
      : "bottom";
  }

  private getOffset(): number {
    const raw = Number(this.triggerElement.dataset.corePopupOffset);
    return Number.isNaN(raw) ? DEFAULT_OFFSET : raw;
  }

  private reposition(): void {
    if (!this.popupElement || !this.isOpen) return;

    const triggerRect = this.triggerElement.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;
    const offset = this.getOffset();
    const availableWidth = viewportWidth - VIEWPORT_PADDING * 2;
    const preferredPosition = this.getPreferredPosition();
    const isFitContent =
      this.popupElement.classList.contains(FIT_CONTENT_CLASS);

    this.applyResponsiveMinWidth(availableWidth);
    this.applyFitContentMaxWidth(isFitContent, availableWidth);

    const popupRect = this.popupElement.getBoundingClientRect();
    const position = this.resolveVerticalPlacement(
      preferredPosition,
      triggerRect,
      popupRect,
      offset,
      viewportHeight,
    );

    const { left, top } = this.computeFixedPosition(
      triggerRect,
      popupRect,
      position,
      offset,
      viewportWidth,
      viewportHeight,
    );

    this.popupElement.style.left = `${left}px`;
    this.popupElement.style.top = `${top}px`;
    this.setPlacementClass(position);
  }

  private resolveVerticalPlacement(
    preferred: PopupPosition,
    triggerRect: DOMRect,
    popupRect: DOMRect,
    offset: number,
    viewportHeight: number,
  ): PopupPosition {
    const fitsTop =
      triggerRect.top >= popupRect.height + offset + VIEWPORT_PADDING;
    const fitsBottom =
      viewportHeight - triggerRect.bottom >=
      popupRect.height + offset + VIEWPORT_PADDING;

    if (preferred === "bottom" && !fitsBottom && fitsTop) return "top";
    if (preferred === "top" && !fitsTop && fitsBottom) return "bottom";
    if (!fitsTop && !fitsBottom) {
      return triggerRect.bottom > viewportHeight - triggerRect.top
        ? "top"
        : "bottom";
    }
    return preferred;
  }

  private computeFixedPosition(
    triggerRect: DOMRect,
    popupRect: DOMRect,
    position: PopupPosition,
    offset: number,
    viewportWidth: number,
    viewportHeight: number,
  ): { left: number; top: number } {
    let left = this.computeAlignedLeft(triggerRect, popupRect);
    if (left + popupRect.width > viewportWidth - VIEWPORT_PADDING) {
      left = viewportWidth - popupRect.width - VIEWPORT_PADDING;
    }
    if (left < VIEWPORT_PADDING) {
      left = VIEWPORT_PADDING;
    }

    let top =
      position === "top"
        ? triggerRect.top - popupRect.height - offset
        : triggerRect.bottom + offset;

    if (top < VIEWPORT_PADDING) {
      top = VIEWPORT_PADDING;
    }
    if (top + popupRect.height > viewportHeight - VIEWPORT_PADDING) {
      top = viewportHeight - popupRect.height - VIEWPORT_PADDING;
    }

    return { left, top };
  }

  /**
   * Alineación horizontal del popup. Por defecto (`start`) el borde izquierdo del popup se
   * alinea con el del disparador, que es el comportamiento histórico del plugin.
   * Con `data-core-popup-align="end" | "center"` se alinea por el borde derecho o el centro.
   * `data-core-popup-align-to="<selector>"` permite alinear contra un ancestro del disparador
   * (o, si no lo encuentra, contra el primer nodo que haga match en el documento) en lugar de
   * contra el propio disparador: útil cuando el disparador es un icono pequeño pero el popup
   * debe alinearse con la columna que lo contiene. La colocación vertical siempre sigue al
   * disparador.
   */
  private computeAlignedLeft(triggerRect: DOMRect, popupRect: DOMRect): number {
    const align = this.getAlign();
    const selector = this.triggerElement.dataset.corePopupAlignTo;

    // Sin `align` ni `align-to` el resultado es exactamente `triggerRect.left`,
    // el comportamiento histórico del plugin.
    if (align === "start" && !selector) return triggerRect.left;

    const anchorRect = selector
      ? this.resolveAnchorRect(selector, triggerRect)
      : triggerRect;

    switch (align) {
      case "end":
        return anchorRect.right - popupRect.width;
      case "center":
        return anchorRect.left + (anchorRect.width - popupRect.width) / 2;
      default:
        return anchorRect.left;
    }
  }

  private getAlign(): PopupAlign {
    const raw = this.triggerElement.dataset.corePopupAlign;
    return raw === "end" || raw === "center" ? raw : "start";
  }

  private resolveAnchorRect(selector: string, triggerRect: DOMRect): DOMRect {
    const anchor =
      this.triggerElement.closest<HTMLElement>(selector) ??
      document.querySelector<HTMLElement>(selector);

    if (!anchor) {
      console.warn(`CorePopup: align anchor "${selector}" not found`);
      return triggerRect;
    }

    return anchor.getBoundingClientRect();
  }

  private handleClickOutside(event: Event): void {
    if (!this.isOpen || !this.popupElement) return;
    const target = event.target as Node;
    if (
      this.triggerElement.contains(target) ||
      this.popupElement.contains(target)
    ) {
      return;
    }
    this.close();
  }

  private handleKeydown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      this.close();
    }
  }

  private applyFitContentMaxWidth(
    isFitContent: boolean,
    availableWidth: number,
  ): void {
    if (!this.popupElement) return;

    if (!isFitContent) {
      this.popupElement.style.maxWidth = "";
      return;
    }

    this.popupElement.style.maxWidth = "none";
    const contentWidth = this.popupElement.scrollWidth;
    if (contentWidth > availableWidth) {
      this.popupElement.style.maxWidth = `${availableWidth}px`;
    }
  }

  private applyResponsiveMinWidth(availableWidth: number): void {
    if (!this.popupElement) return;

    if (this.baseMinWidthPx != null && this.baseMinWidthPx > 0) {
      this.popupElement.style.minWidth = `${Math.min(
        this.baseMinWidthPx,
        availableWidth,
      )}px`;
      return;
    }

    this.popupElement.style.minWidth = "";
  }

  /**
   * Lee el min-width configurado en CSS, ignorando el inline de un layout previo.
   * Debe llamarse tras init y al abrir para no arrastrar valores clampados.
   */
  private refreshBaseMinWidthFromCss(): void {
    if (!this.popupElement) return;
    this.baseMinWidthPx = this.readConfiguredMinWidthPx();
  }

  private readConfiguredMinWidthPx(): number | null {
    if (!this.popupElement) return null;

    const prevInline = this.popupElement.style.minWidth;
    this.popupElement.style.minWidth = "";

    const computed = Number.parseFloat(
      window.getComputedStyle(this.popupElement).minWidth,
    );

    this.popupElement.style.minWidth = prevInline;

    if (!Number.isFinite(computed) || computed <= 0) {
      return null;
    }

    return computed;
  }

  private setPlacementClass(position: PopupPosition): void {
    if (!this.popupElement) return;
    this.popupElement.classList.remove(
      PLACEMENT_TOP_CLASS,
      PLACEMENT_BOTTOM_CLASS,
    );
    this.popupElement.classList.add(
      position === "top" ? PLACEMENT_TOP_CLASS : PLACEMENT_BOTTOM_CLASS,
    );
  }

  destroy(): void {
    this.close();
    this.triggerElement.removeEventListener("click", this.boundToggle);
    this.popupElement?.removeEventListener("click", this.boundCloseButtonClick);
    window.removeEventListener("resize", this.boundReposition);
    window.removeEventListener("scroll", this.boundReposition, true);
    document.removeEventListener("click", this.boundClickOutside);
    document.removeEventListener("keydown", this.boundKeydown);

    if (this.popupElement) {
      this.popupElement.style.top = "";
      this.popupElement.style.left = "";
      this.popupElement.style.minWidth = "";
      this.popupElement.style.maxWidth = "";
      this.popupElement.setAttribute("aria-hidden", "true");
      this.popupElement.classList.remove(
        PLACEMENT_TOP_CLASS,
        PLACEMENT_BOTTOM_CLASS,
      );
    }
    this.baseMinWidthPx = null;

    if (this.popupElement && this.placeholderNode && this.originalParent) {
      this.originalParent.insertBefore(this.popupElement, this.placeholderNode);
      this.placeholderNode.remove();
      this.placeholderNode = null;
    }
  }
}

PluginRegistry.register("core-popup", CorePopupPlugin);
